"use client";

import { datadogRum } from "@datadog/browser-rum";
import Script from "next/script";

import { useClientEnvironment } from "src/app/environment/client";

import analyticsIntegrations from "../../app/analytics/analytics-client";

import { buildSegmentSnippet } from "./AnalyticsScripts.utils";

/**
 * Segment's OneTrust consent wrapper, `global` build — it defines `window.withOneTrust`.
 * Pinned rather than `@latest`: this runs before analytics on every page load.
 */
const SEGMENT_ONETRUST_WRAPPER_URL =
  "https://cdn.jsdelivr.net/npm/@segment/analytics-consent-wrapper-onetrust@1.1.3/dist/umd/analytics-onetrust.global.js";

// putting analyticsIntegrations on window if it exists
// and the before the event is sent to segment, we check to see if analytics was loaded
// this ensures that the event isn't sent if user didn't give consent

if (typeof window !== "undefined") {
  window.analyticsIntegrations = analyticsIntegrations;
}

export default function AnalyticsScripts() {
  const { SEGMENT_CUSTOM_DOMAIN, SEGMENT_IO_API_KEY } = useClientEnvironment();

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-before-interactive-script-outside-document -- the rule only permits files under `app/`; the root layout renders this, which is the placement Next documents. */}
      <Script
        id="segment-script"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: buildSegmentSnippet({
            segmentCustomDomain: SEGMENT_CUSTOM_DOMAIN,
            segmentKey: SEGMENT_IO_API_KEY,
          }),
        }}
      />

      {/*
        The snippet above only queues calls; nothing reaches Segment until `analytics.load`,
        which this wrapper owns (formerly `@segment/consent-manager`). It waits for the
        OneTrust SDK, then gates each destination on its mapped consent group.
      */}
      <Script
        id="segment-onetrust-wrapper"
        src={SEGMENT_ONETRUST_WRAPPER_URL}
        onError={() => {
          // Blocked by an extension, 404, network failure, or SRI mismatch — none of which
          // reach `onLoad`. RUM is the only channel left, same as below.
          datadogRum.addError(
            new Error(
              "Segment OneTrust consent wrapper failed to load — Segment will not load.",
            ),
          );
        }}
        onLoad={() => {
          if (!window.withOneTrust) {
            // Segment cannot report its own absence, so RUM is the only channel left.
            datadogRum.addError(
              new Error(
                "Segment OneTrust consent wrapper unavailable — Segment will not load.",
              ),
            );

            return;
          }

          const { withOneTrust } = window;

          // Waits for the old banner's choice to reach OneTrust, so a past "no" is honoured
          // from the first event. Undefined when OneTrust isn't rendered, which resolves at once.
          void Promise.resolve(window.psLegacyConsentMigration).then(() => {
            // `apiHost` routes event *ingestion* through the first-party domain; the
            // snippet's `_cdn`/`src` only route the CDN, so without it events bypass the proxy.
            withOneTrust(window.analytics).load(SEGMENT_IO_API_KEY, {
              integrations: {
                "Segment.io": { apiHost: `${SEGMENT_CUSTOM_DOMAIN}/v1` },
              },
            });
          });
        }}
      />
    </>
  );
}
